<template>
    <div class="export-history-list">
        <div v-if="loading" class="text-center py-3">
            <b-spinner small />
            Loading history...
        </div>

        <div v-else-if="!history.length" class="text-center text-muted py-3">
            No execution history yet.
        </div>

        <b-table v-else :items="history" :fields="fields" small striped hover responsive>
            <template #cell(executionStart)="data">
                {{ formatDateTime(data.value) }}
            </template>

            <template #cell(executionEnd)="data">
                {{ data.value ? formatDateTime(data.value) : '-' }}
            </template>

            <template #cell(status)="data">
                <b-badge :variant="getStatusVariant(data.value)">
                    {{ data.value }}
                </b-badge>
            </template>

            <template #cell(deliveryStatus)="data">
                <b-badge v-if="data.value" :variant="getDeliveryStatusVariant(data.value)">
                    {{ data.value }}
                </b-badge>
                <span v-else class="text-muted">-</span>
            </template>

            <template #cell(fileSize)="data">
                {{ data.value ? formatFileSize(data.value) : '-' }}
            </template>

            <template #cell(errorMessage)="data">
                <span v-if="data.value" class="text-danger" :title="data.value">
                    <i class="fas fa-exclamation-circle"></i>
                    {{ truncate(data.value, 30) }}
                </span>
                <span v-else class="text-muted">-</span>
            </template>
        </b-table>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { ReportExportHistory, reportExportService } from "@/services/reportExportService";

@Component
export default class ExportHistoryList extends Vue {
    @Prop({ required: true }) scheduleId!: number;
    @Prop({ default: 10 }) limit!: number;

    private history: ReportExportHistory[] = [];
    private loading = false;

    private fields = [
        { key: "executionStart", label: "Started", sortable: true },
        { key: "executionEnd", label: "Ended", sortable: true },
        { key: "status", label: "Status" },
        { key: "deliveryStatus", label: "Delivery" },
        { key: "fileSize", label: "Size" },
        { key: "deliveryAttempts", label: "Attempts" },
        { key: "errorMessage", label: "Error" }
    ];

    mounted() {
        this.loadHistory();
    }

    @Watch("scheduleId")
    onScheduleIdChange() {
        this.loadHistory();
    }

    public async loadHistory() {
        if (!this.scheduleId) return;

        this.loading = true;
        try {
            this.history = await reportExportService.getScheduleHistory(this.scheduleId, this.limit);
        } catch (error) {
            console.error("Failed to load history:", error);
        } finally {
            this.loading = false;
        }
    }

    private formatDateTime(dateStr: string): string {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        return date.toLocaleString();
    }

    private formatFileSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    private getStatusVariant(status: string): string {
        switch (status?.toLowerCase()) {
            case "completed":
                return "success";
            case "failed":
                return "danger";
            case "exporting":
            case "delivering":
                return "info";
            case "pending":
                return "warning";
            default:
                return "secondary";
        }
    }

    private getDeliveryStatusVariant(status: string): string {
        switch (status?.toLowerCase()) {
            case "delivered":
                return "success";
            case "failed":
                return "danger";
            case "inprogress":
                return "info";
            case "pending":
                return "warning";
            default:
                return "secondary";
        }
    }

    private truncate(text: string, maxLength: number): string {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    }
}
</script>

<style scoped>
.export-history-list {
    max-height: 300px;
    overflow-y: auto;
}
</style>
